/**
 * Client-side logging facade (mirrors web's `lib/log.ts`).
 *
 *  - `debug`/`info` only reach the console in dev (Vite `import.meta.env.DEV`) or
 *    when `?debug=1` / localStorage `recruiter:debug` is set.
 *  - `warn`/`error` always print so operators see them.
 *
 * DIAG SINK: `error` is ALSO forwarded to RUM's client-error queue
 * (`reportClientError`, kind="log"), which batches to POST /api/client-errors so
 * talent-side crashes surface in Cloud Logging. It is a no-op until initRum runs +
 * samples the page in, so importing it is free on a sampled-out load.
 *
 * Signature is (scope, message, data?, error?) — `scope` is a short subsystem tag
 * like "talent-stream" so logs stay greppable.
 */

import { reportClientError } from "@recruiter/shared";

type LogData = Record<string, unknown>;

function debugEnabled(): boolean {
  if (import.meta.env.DEV) return true;
  if (typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).get("debug") === "1") return true;
    return window.localStorage.getItem("recruiter:debug") === "1";
  } catch {
    return false;
  }
}

function fmt(scope: string, message: string): string {
  return `[${scope}] ${message}`;
}

export const log = {
  debug(scope: string, message: string, data?: LogData): void {
    if (!debugEnabled()) return;
    console.debug(fmt(scope, message), data ?? "");
  },
  info(scope: string, message: string, data?: LogData): void {
    if (!debugEnabled()) return;
    console.info(fmt(scope, message), data ?? "");
  },
  warn(scope: string, message: string, data?: LogData): void {
    console.warn(fmt(scope, message), data ?? "");
  },
  error(scope: string, message: string, data?: LogData, error?: unknown): void {
    console.error(fmt(scope, message), data ?? "", error ?? "");
    reportClientError(scope, message, data, error);
  },
};

export type Logger = typeof log;
